import type { ReactElement } from 'react'
import Container from '@cloudscape-design/components/container'
import Header from '@cloudscape-design/components/header'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Box from '@cloudscape-design/components/box'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs'
import type { ContentType, ContentTypeStatus, NodeProgress, NodeStatus, TokenUsage } from '@/lib/types'
import styles from './PipelinePanel.module.css'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface PipelinePanelProps {
  nodeProgress?: NodeProgress[]
  nodeStatuses?: Record<string, NodeStatus>
  activeNode: string | null
  typeStatuses: Record<string, ContentTypeStatus>
  selectedTypes: ContentType[]
  estimatedCostUsd: number
  tokenUsage: TokenUsage
  progressPct?: number
  isFailed?: boolean
  activeModel?: string | null
}

// ─── Pipeline nodes in execution order ───────────────────────────────────────

const PIPELINE_NODES = [
  'resolve_client',
  'persona_builder',
  'strategy_planner',
  'campaign_writer',
  'tone_checker',
  'ethics_guardrail',
  'output_formatter',
] as const

// ─── Node label formatting ────────────────────────────────────────────────────

function formatNodeLabel(node: string): string {
  return node
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// ─── Platform icon helper ─────────────────────────────────────────────────────

const PP_IG_GRAD_ID = 'pp-ig-grad'
const PP_IGA_GRAD_ID = 'pp-iga-grad'

function getPlatformIcon(type: ContentType): ReactElement {
  switch (type) {
    case 'facebook_post':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#1877F2" />
          <path d="M16 8h-2.5C12.67 8 12 8.67 12 9.5V11h4l-.5 4h-3.5v8H8v-8H5v-4h3V9.5C8 6.46 10.46 4 13.5 4H16v4z" fill="#fff" />
        </svg>
      )
    case 'twitter_post':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#000" />
          <path d="M17.5 5h-2.3L12 9.4 9.8 5H5l5.4 7.4L5 19h2.3L12 14.1l2.7 4.9H20l-5.7-7.8L17.5 5z" fill="#fff" />
        </svg>
      )
    case 'instagram_post':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={styles.icon}>
          <defs>
            <linearGradient id={PP_IG_GRAD_ID} x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#F58529" />
              <stop offset="50%" stopColor="#DD2A7B" />
              <stop offset="100%" stopColor="#515BD4" />
            </linearGradient>
          </defs>
          <rect width="24" height="24" rx="5" fill={`url(#${PP_IG_GRAD_ID})`} />
          <rect x="6" y="6" width="12" height="12" rx="3" stroke="#fff" strokeWidth="1.5" fill="none" />
          <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="1.5" fill="none" />
          <circle cx="16.5" cy="7.5" r="1" fill="#fff" />
        </svg>
      )
    case 'linkedin_post':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#0A66C2" />
          <path d="M7 9h2v9H7V9zm1-1a1.25 1.25 0 110-2.5A1.25 1.25 0 018 8zm4 1h2v1.2c.4-.7 1.3-1.4 2.5-1.4C18.4 8.8 19 10.1 19 12v6h-2v-5.5c0-1-.3-1.7-1.2-1.7-.9 0-1.4.6-1.6 1.2-.1.2-.2.5-.2.8V18h-2V9z" fill="#fff" />
        </svg>
      )
    case 'email_outreach':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#EA4335" />
          <path d="M5 8l7 5 7-5V7H5v1zm0 1.5V17h14V9.5l-7 5-7-5z" fill="#fff" />
        </svg>
      )
    case 'blog_seo':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#4285F4" />
          <rect x="6" y="7" width="8" height="2" rx="1" fill="#fff" />
          <rect x="6" y="11" width="12" height="2" rx="1" fill="#fff" />
          <rect x="6" y="15" width="6" height="2" rx="1" fill="#fff" />
          <path d="M16 14l1.5 1.5L20 13" stroke="#34A853" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'facebook_ad':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#1877F2" opacity="0.6" />
          <path d="M16 8h-2.5C12.67 8 12 8.67 12 9.5V11h4l-.5 4h-3.5v8H8v-8H5v-4h3V9.5C8 6.46 10.46 4 13.5 4H16v4z" fill="#fff" />
          <circle cx="18" cy="18" r="5" fill="#F5C518" />
          <path d="M16.5 18l1 1 2-2" stroke="#333" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'instagram_ad':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={styles.icon}>
          <defs>
            <linearGradient id={PP_IGA_GRAD_ID} x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#F58529" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#DD2A7B" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#515BD4" stopOpacity="0.6" />
            </linearGradient>
          </defs>
          <rect width="24" height="24" rx="5" fill={`url(#${PP_IGA_GRAD_ID})`} />
          <rect x="6" y="6" width="12" height="12" rx="3" stroke="#fff" strokeWidth="1.5" fill="none" />
          <circle cx="12" cy="12" r="3" stroke="#fff" strokeWidth="1.5" fill="none" />
          <circle cx="16.5" cy="7.5" r="1" fill="#fff" />
          <circle cx="18" cy="18" r="5" fill="#F5C518" />
          <path d="M16.5 18l1 1 2-2" stroke="#333" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    case 'google_ads':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#202124" />
          <circle cx="8" cy="12" r="3" fill="#FBBC04" />
          <circle cx="14" cy="12" r="3" fill="#34A853" />
          <circle cx="18" cy="12" r="3" fill="#4285F4" />
        </svg>
      )
    case 'gbp_post':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" className={styles.icon}>
          <rect width="24" height="24" rx="4" fill="#202124" />
          <path d="M12 5a5 5 0 00-5 5c0 3.5 5 9 5 9s5-5.5 5-9a5 5 0 00-5-5zm0 7a2 2 0 110-4 2 2 0 010 4z" fill="#EA4335" />
        </svg>
      )
  }
}

// ─── Content type labels ──────────────────────────────────────────────────────

const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  facebook_post: 'Facebook',
  twitter_post: 'Twitter/X',
  instagram_post: 'Instagram',
  linkedin_post: 'LinkedIn',
  email_outreach: 'Email',
  blog_seo: 'Blog/SEO',
  facebook_ad: 'FB Ad',
  instagram_ad: 'IG Ad',
  google_ads: 'Google Ads',
  gbp_post: 'Google Biz',
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function PipelinePanel({
  nodeProgress,
  nodeStatuses,
  activeNode,
  typeStatuses,
  selectedTypes,
  estimatedCostUsd,
  tokenUsage,
  progressPct,
  isFailed,
  activeModel,
}: PipelinePanelProps) {
  const hasApiProgress = (nodeProgress?.length ?? 0) > 0

  function renderStepRow(name: string, idx: number, isActive: boolean, isDone: boolean) {
    const numClass = [
      styles.stepNum,
      isDone ? styles.stepNumDone : isActive ? styles.stepNumActive : styles.stepNumPending,
    ].filter(Boolean).join(' ')

    return (
      <div key={name} className={styles.stepRow}>
        <Box variant="span" className={numClass}>
          {isDone ? '✓' : idx + 1}
        </Box>
        {isDone ? (
          <StatusIndicator type="success">{formatNodeLabel(name)}</StatusIndicator>
        ) : isActive ? (
          <StatusIndicator type="in-progress">
            <Box variant="span" fontWeight="bold">{formatNodeLabel(name)}</Box>
          </StatusIndicator>
        ) : (
          <Box color="text-body-secondary">{formatNodeLabel(name)}</Box>
        )}
      </div>
    )
  }

  return (
    <Container header={<Header variant="h3">Run Status</Header>}>
      <SpaceBetween size="l">

        {/* Pipeline steps */}
        <SpaceBetween size="s">
          <Box variant="awsui-key-label">Steps</Box>
          {progressPct != null && progressPct > 0 && (
            <progress value={progressPct} max={100} style={{ width: '100%' }} />
          )}
          <SpaceBetween size="xs">
            {hasApiProgress
              ? nodeProgress!.map((node, idx) => {
                  const isActive = node.status === 'running' || activeNode === node.name
                  const isDone = node.status === 'complete'
                  return renderStepRow(node.name, idx, isActive, isDone)
                })
              : PIPELINE_NODES.map((node, idx) => {
                  const status = nodeStatuses?.[node] ?? 'pending'
                  const isActive = status === 'active' || activeNode === node
                  const isDone = status === 'done'
                  return renderStepRow(node, idx, isActive, isDone)
                })}
          </SpaceBetween>
        </SpaceBetween>

        {/* Content types */}
        {selectedTypes.length > 0 && (
          <SpaceBetween size="s">
            <Box variant="awsui-key-label">Content</Box>
            <SpaceBetween size="xs">
              {selectedTypes.map(type => {
                const status: ContentTypeStatus = typeStatuses[type] ?? 'pending'
                const label = CONTENT_TYPE_LABELS[type]

                let indicator: ReactElement
                if (status === 'done') {
                  indicator = <StatusIndicator type="success">Done</StatusIndicator>
                } else if (status === 'generating') {
                  indicator = <StatusIndicator type="in-progress">Generating</StatusIndicator>
                } else if (isFailed) {
                  indicator = <StatusIndicator type="error">Failed</StatusIndicator>
                } else {
                  indicator = <StatusIndicator type="pending">Queued</StatusIndicator>
                }

                return (
                  <div key={type} className={styles.typeRow}>
                    <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
                      {getPlatformIcon(type)}
                      <Box>{label}</Box>
                    </SpaceBetween>
                    {indicator}
                  </div>
                )
              })}
            </SpaceBetween>
          </SpaceBetween>
        )}

        {/* Run cost */}
        <KeyValuePairs
          columns={1}
          items={[
            ...(activeModel ? [{ label: 'Model', value: activeModel }] : []),
            { label: 'Estimated cost', value: `$${estimatedCostUsd.toFixed(3)}` },
            { label: 'Tokens used', value: tokenUsage.total_tokens.toLocaleString() },
          ]}
        />

      </SpaceBetween>
    </Container>
  )
}
